import { useState } from "react";

const PUPIL_OPTIONS = ["PEARL", "Equal & reactive", "Sluggish", "Dilated", "Constricted", "Fixed", "Unequal"];
const CONSCIOUSNESS_OPTIONS = ["Alert", "Voice", "Pain", "Unresponsive"];

const NUM_FIELDS = [
  { key: "heart_rate", label: "Heart Rate", unit: "bpm", testid: "input-heart-rate" },
  { key: "bp_systolic", label: "BP Systolic", unit: "mmHg", testid: "input-blood-pressure" },
  { key: "bp_diastolic", label: "BP Diastolic", unit: "mmHg", testid: "input-bp-diastolic" },
  { key: "respiratory_rate", label: "Resp Rate", unit: "/min", testid: "input-respiratory-rate" },
  { key: "spo2", label: "SpO2", unit: "%", testid: "input-sp02" },
  { key: "temperature", label: "Temperature", unit: "°C", step: "0.1", testid: "input-temperature" },
  { key: "blood_glucose", label: "Blood Glucose", unit: "mmol/L", step: "0.1", testid: "input-blood-glucose" },
  { key: "pupil_left_mm", label: "Pupil Left", unit: "mm", step: "0.5", testid: "input-pupil-left" },
  { key: "pupil_right_mm", label: "Pupil Right", unit: "mm", step: "0.5", testid: "input-pupil-right" },
];

export default function ObservationForm({ initial, onSubmit, onCancel, submitting }) {
  const [form, setForm] = useState(() => {
    const base = {};
    NUM_FIELDS.forEach((f) => (base[f.key] = initial?.[f.key] != null ? String(initial[f.key]) : ""));
    base.pupils = initial?.pupils || "";
    base.consciousness = initial?.consciousness || "Alert";
    base.gcs_eyes = initial?.gcs_eyes != null ? String(initial.gcs_eyes) : "";
    base.gcs_verbal = initial?.gcs_verbal != null ? String(initial.gcs_verbal) : "";
    base.gcs_motor = initial?.gcs_motor != null ? String(initial.gcs_motor) : "";
    base.comment = initial?.comment || "";
    return base;
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const payload = {};
    NUM_FIELDS.forEach((f) => {
      if (form[f.key] !== "") payload[f.key] = parseFloat(form[f.key]);
    });
    if (form.pupils) payload.pupils = form.pupils;
    if (form.consciousness) payload.consciousness = form.consciousness;
    for (const k of ["gcs_eyes", "gcs_verbal", "gcs_motor"]) {
      if (form[k] !== "") payload[k] = parseFloat(form[k]);
    }
    if (form.comment.trim()) payload.comment = form.comment.trim();
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} data-testid="observation-form" className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {NUM_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              {f.label} <span className="font-normal text-slate-400">({f.unit})</span>
            </label>
            <input
              data-testid={f.testid}
              type="number"
              inputMode="decimal"
              step={f.step || "1"}
              value={form[f.key]}
              onChange={(e) => set(f.key, e.target.value)}
              className="w-full h-12 rounded-lg border border-slate-300 px-3 text-base font-vitals font-bold focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
            />
          </div>
        ))}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Pupils</label>
          <select data-testid="input-pupils" value={form.pupils} onChange={(e) => set("pupils", e.target.value)}
            className="w-full h-12 rounded-lg border border-slate-300 px-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white">
            <option value="">Not assessed</option>
            {PUPIL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Consciousness (AVPU)</label>
          <select data-testid="input-gcs-avpu" value={form.consciousness} onChange={(e) => set("consciousness", e.target.value)}
            className="w-full h-12 rounded-lg border border-slate-300 px-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white">
            {CONSCIOUSNESS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div className="col-span-2 sm:col-span-3 rounded-lg border border-slate-200 p-3" data-testid="gcs-block">
          <p className="text-xs font-semibold text-slate-600 mb-2">
            GCS score <span className="font-normal text-slate-400">— Eyes / Voice / Motor, total {[form.gcs_eyes, form.gcs_verbal, form.gcs_motor].filter((v) => v !== "").reduce((a, b) => a + Number(b), 0)}/15</span>
          </p>
          <div className="grid grid-cols-3 gap-3">
            {[{ k: "gcs_eyes", label: "Eyes (1–4)", max: 4, testid: "input-gcs-eyes" },
              { k: "gcs_verbal", label: "Voice (1–5)", max: 5, testid: "input-gcs-verbal" },
              { k: "gcs_motor", label: "Motor (1–6)", max: 6, testid: "input-gcs-motor" }].map((f) => (
              <div key={f.k}>
                <label className="block text-[10px] font-semibold text-slate-500 mb-1">{f.label}</label>
                <input data-testid={f.testid} type="number" inputMode="numeric" min="1" max={f.max} step="1"
                  value={form[f.k]} onChange={(e) => set(f.k, e.target.value)}
                  className="w-full h-12 rounded-lg border border-slate-300 px-3 text-base font-vitals font-bold focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Observation comment (optional)</label>
        <textarea data-testid="input-observation-comment" rows={2} value={form.comment} onChange={(e) => set("comment", e.target.value)}
          placeholder="e.g. Patient complaining of dizziness on standing"
          className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
      </div>
      <div className="flex justify-end gap-3">
        {onCancel && (
          <button type="button" onClick={onCancel}
            className="h-12 px-5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-600 hover:border-slate-400 transition-colors">
            Cancel
          </button>
        )}
        <button type="submit" disabled={submitting} data-testid="save-observation-button"
          className="h-12 px-6 rounded-lg bg-[#007F3B] hover:bg-[#006631] transition-colors text-white text-sm font-semibold disabled:opacity-60 min-w-[160px]">
          {submitting ? "Saving…" : initial ? "Save changes" : "Save observation"}
        </button>
      </div>
    </form>
  );
}
